import { __awaiter } from 'tslib';
import Provider from '@walletconnect/universal-provider';
import { ethers } from 'ethers';
import { getWalletBookWallet } from '@dynamic-labs/wallet-book';
import { normalizeWalletName } from '../../utils/normalizeWalletName/normalizeWalletName.js';
import { EthProvider } from '../ethereum/ethProvider.js';
import { isMobile } from '../../utils/isMobile.js';
import { getDeepLink } from '../clients/walletConnect/walletConnect.js';
import { DynamicError } from '../../errors.js';
import { logger } from '../../utils/logger.js';
import { utf8ToHex } from '../../utils/encoding.js';

let provider;
let createSession;
const getInitializedProvider = (providerOpts, evmNetworks) => __awaiter(void 0, void 0, void 0, function* () {
    if (!provider) {
        provider = yield Provider.init(providerOpts);
        const connection = yield provider.client.connect({
            requiredNamespaces: {
                eip155: {
                    chains: evmNetworks
                        // filtering out Palm for now since it causes Trust to crash
                        .filter((network) => network.chainId !== 11297108109)
                        .map((network) => `eip155:${network.chainId}`),
                    events: ['chainChanged', 'accountsChanged'],
                    methods: [
                        'eth_sendTransaction',
                        'eth_signTransaction',
                        'eth_sign',
                        'personal_sign',
                        'eth_signTypedData',
                    ],
                },
            },
        });
        // eslint-disable-next-line prefer-destructuring
        provider.uri = connection.uri;
        createSession = connection.approval;
    }
    return provider;
});
const performPlatformSpecificConnectionMethod = (uri, metadata, opts) => {
    var _a, _b, _c;
    if (isMobile()) {
        const deepLink = getDeepLink(uri, metadata);
        window.location.href = deepLink;
    }
    else {
        if ((_a = metadata === null || metadata === void 0 ? void 0 : metadata.desktop) === null || _a === void 0 ? void 0 : _a.native) {
            const desktopUri = getDeepLink(uri, metadata);
            (_b = opts === null || opts === void 0 ? void 0 : opts.onDesktopUri) === null || _b === void 0 ? void 0 : _b.call(opts, desktopUri);
        }
        (_c = opts === null || opts === void 0 ? void 0 : opts.onDisplayUri) === null || _c === void 0 ? void 0 : _c.call(opts, uri);
    }
};
const sessionTopicKey = (walletName) => `dynamic-wc2-session-topic-${walletName}`;
class WalletConnectV2 extends EthProvider {
    constructor({ evmNetworks, walletName, }) {
        var _a;
        super({ evmNetworks });
        this.supportedChains = ['EVM', 'ETH'];
        this.connectedChain = 'EVM';
        this.canConnectViaQrCode = true;
        this.canConnectViaCustodialService = false;
        this.isWalletConnect = true;
        this.switchNetworkOnlyFromWallet = false;
        this.name = walletName;
        if ((_a = provider === null || provider === void 0 ? void 0 : provider.session) === null || _a === void 0 ? void 0 : _a.topic) {
            if (localStorage.getItem(this.sessionTopicKey) === provider.session.topic) {
                this.session = provider.session;
            }
        }
    }
    get sessionTopicKey() {
        return sessionTopicKey(this.key);
    }
    get key() {
        return normalizeWalletName(this.name);
    }
    supportsNetworkSwitching() {
        return true;
    }
    setupEventListeners(listeners) {
        if (!provider) {
            return;
        }
        provider.client.on('session_event', ({ params }) => {
            var _a, _b;
            if (!params || !params.event) {
                logger.debug('session_event was called without params or params.event');
                return;
            }
            const { name, data } = params.event;
            if (name === 'chainChanged') {
                if (typeof data !== 'string') {
                    logger.debug(`received unexpected data for chainChanged: ${data} with type ${typeof data}}`);
                    return;
                }
                (_a = listeners.onChainChange) === null || _a === void 0 ? void 0 : _a.call(listeners, data);
            }
            else if (name === 'accountsChanged') {
                if (typeof data !== 'string' || !Array.isArray(data)) {
                    logger.debug(`received unexpected data for accountsChanged: ${data} with type ${typeof data}}`);
                    return;
                }
                (_b = listeners.onAccountChange) === null || _b === void 0 ? void 0 : _b.call(listeners, data);
            }
        });
        provider.client.on('session_delete', () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            localStorage.removeItem(this.sessionTopicKey);
            yield ((_a = listeners.onDisconnect) === null || _a === void 0 ? void 0 : _a.call(listeners));
        }));
    }
    teardownEventListeners() {
        if (!provider) {
            return;
        }
        provider.client.removeAllListeners('session_event');
        provider.client.removeAllListeners('session_delete');
    }
    getDeepLink() {
        var _a;
        if (!(provider === null || provider === void 0 ? void 0 : provider.uri))
            return;
        const wallet = getWalletBookWallet(this.name);
        if (!isMobile() && !((_a = wallet.desktop) === null || _a === void 0 ? void 0 : _a.native)) {
            return undefined;
        }
        return getDeepLink(provider.uri, wallet);
    }
    getWeb3Provider() {
        if (!provider) {
            throw new Error('No provider found');
        }
        return new ethers.providers.Web3Provider(provider, 'any');
    }
    isInstalledOnBrowser() {
        return false;
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.fetchPublicAddress();
        });
    }
    fetchPublicAddress(opts) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.session) {
                return this.session.namespaces.eip155.accounts[0].split(':')[2];
            }
            const metadata = getWalletBookWallet(this.name);
            if (!provider) {
                throw new DynamicError('No provider found');
            }
            if (!provider.uri || !createSession) {
                throw new DynamicError('No uri or approval found');
            }
            performPlatformSpecificConnectionMethod(provider.uri, metadata, {
                onDesktopUri: opts === null || opts === void 0 ? void 0 : opts.onDesktopUri,
                onDisplayUri: opts === null || opts === void 0 ? void 0 : opts.onDisplayUri,
            });
            const session = yield createSession();
            if (!session) {
                return undefined;
            }
            this.session = session;
            localStorage.setItem(this.sessionTopicKey, session.topic);
            // need to "reconnect" the provider so that we can tell it what the rpc urls are
            yield provider.connect({
                namespaces: {
                    eip155: {
                        chains: this.evmNetworks
                            // filtering out Palm for now since it causes Trust to crash
                            .filter((network) => network.chainId !== 11297108109)
                            .map((network) => `eip155:${network.chainId}`),
                        events: ['chainChanged', 'accountsChanged'],
                        methods: [
                            'eth_sendTransaction',
                            'eth_signTransaction',
                            'eth_sign',
                            'personal_sign',
                            'eth_signTypedData',
                        ],
                        rpcMap: this.evmNetworkRpcMap(),
                    },
                },
                skipPairing: true,
            });
            // THIS LINE IS SUPER IMPORTANT
            // since we are skipping pairing, (see: skipPairing: true above), we need to manually set the session
            // so that future provider requests work (like getNetwork)
            provider.session = session;
            return session.namespaces.eip155.accounts[0].split(':')[2];
        });
    }
    signMessage(messageToSign) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.session) {
                throw new DynamicError('no session');
            }
            if (!provider) {
                throw new DynamicError('No WalletConnect provider found to handle signing');
            }
            const metadata = getWalletBookWallet(this.name);
            if (isMobile()) {
                // for sign message, no uri is needed because the uri encodes connection details,
                // and at this point we are already connected
                const deepLink = getDeepLink('', metadata);
                window.location.href = deepLink;
            }
            // eslint-disable-next-line prefer-destructuring
            const address = this.session.namespaces.eip155.accounts[0].split(':')[2];
            const web3Provider = this.getWeb3Provider();
            return web3Provider.send('personal_sign', [
                utf8ToHex(messageToSign, true),
                address,
            ]);
        });
    }
    proveOwnership(messageToSign) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.signMessage(messageToSign);
        });
    }
    endSession() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.session) {
                return;
            }
            if (!provider) {
                return;
            }
            try {
                yield provider.disconnect();
                localStorage.removeItem(this.sessionTopicKey);
            }
            catch (e) {
                logger.debug(e);
            }
        });
    }
    providerSwitchNetwork({ network, provider, }) {
        const _super = Object.create(null, {
            providerSwitchNetwork: { get: () => super.providerSwitchNetwork }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const currentNetworkId = yield this.getNetwork();
            if (currentNetworkId && currentNetworkId === network.chainId) {
                return;
            }
            if (this.switchNetworkOnlyFromWallet) {
                throw new DynamicError('Network switching is only supported through the wallet');
            }
            if (!this.supportsNetworkSwitching()) {
                throw new DynamicError('Network switching not supported');
            }
            if (!provider) {
                throw new DynamicError('Provider not found');
            }
            return _super.providerSwitchNetwork.call(this, { network, provider });
        });
    }
    getConnectedAccounts() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.session) {
                return [];
            }
            return [this.session.namespaces.eip155.accounts[0].split(':')[2]];
        });
    }
}

export { WalletConnectV2 as default, getInitializedProvider };
